import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
    LogOut, Search, Package, RefreshCw, ChevronLeft, ChevronRight,
    TrendingUp, DollarSign, Clock, Filter, Upload, X, ChevronDown,
    Camera,
} from "lucide-react";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL;
const STATUSES = ["all", "placed", "confirmed", "out_for_delivery", "delivered", "cancelled"];

const adminApi = axios.create({
    baseURL: `${BASE}/api/admin`,
    headers: { "Content-Type": "application/json" },
});

const statusBadge = (status) => {
    const map = {
        placed: "bg-blue-100 text-blue-700 border-blue-200",
        confirmed: "bg-amber-100 text-amber-700 border-amber-200",
        out_for_delivery: "bg-purple-100 text-purple-700 border-purple-200",
        delivered: "bg-emerald-100 text-emerald-700 border-emerald-200",
        cancelled: "bg-rose-100 text-rose-700 border-rose-200",
    };
    return map[status] || "bg-neutral-100 text-neutral-700 border-neutral-200";
};

const fmt = (all) => `L ${Number(all || 0).toLocaleString()}`;

const ProductsTab = ({ authHeader }) => {
    const [products, setProducts] = useState([]);
    const [total, setTotal] = useState(0);
    const [pages, setPages] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [brand, setBrand] = useState("");
    const [category, setCategory] = useState("");
    const [facets, setFacets] = useState({ brands: [], categories: [] });
    const [edits, setEdits] = useState({});       // pid → {field: value}
    const [savingId, setSavingId] = useState(null);

    // Debounced search
    const [debSearch, setDebSearch] = useState("");
    useEffect(() => {
        const t = setTimeout(() => { setDebSearch(search); setPage(1); }, 350);
        return () => clearTimeout(t);
    }, [search]);

    // Load facets once
    useEffect(() => {
        adminApi.get("/products/facets", { headers: authHeader() })
            .then(r => setFacets(r.data))
            .catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, limit: 50 };
            if (debSearch) params.q = debSearch;
            if (brand)    params.brand = brand;
            if (category) params.category = category;
            const r = await adminApi.get("/products", { headers: authHeader(), params });
            setProducts(r.data.products);
            setTotal(r.data.total);
            setPages(r.data.pages);
            setEdits({}); // clear stale row edits after reload
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not load products");
        } finally {
            setLoading(false);
        }
    }, [page, debSearch, brand, category]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const setField = (pid, field, val) => {
        setEdits(prev => ({ ...prev, [pid]: { ...(prev[pid] || {}), [field]: val } }));
    };

    const uploadImage = async (p, fileObj) => {
        if (!fileObj) return;
        setSavingId(p.id);
        try {
            const fd = new FormData();
            fd.append("file", fileObj);
            const r = await adminApi.post(`/products/${p.id}/image`, fd, {
                headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
            });
            const np = r.data.product;
            setProducts(prev => prev.map(x => x.id === p.id ? { ...x, ...np } : x));
            toast.success(`Image updated for “${p.name_en}”`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Image upload failed");
        } finally {
            setSavingId(null);
        }
    };

    const saveRow = async (p) => {
        const e = edits[p.id];
        if (!e || Object.keys(e).length === 0) return;
        const payload = {};
        if (e.price_all !== undefined)    payload.price_all = Number(e.price_all);
        if (e.discount_pct !== undefined) payload.discount_pct = Number(e.discount_pct);
        if (e.stock !== undefined)        payload.stock = parseInt(e.stock, 10);
        if (e.product_code !== undefined) payload.product_code = String(e.product_code).trim();
        if (e.ean_barcode !== undefined)  payload.ean_barcode  = String(e.ean_barcode).trim();
        if (e.category !== undefined) {
            // Map slug back to its display label so both stay in sync
            const cat = facets.categories.find(c => c.id === e.category);
            payload.category = e.category;
            if (cat) payload.category_label = cat.label;
        }
        setSavingId(p.id);
        try {
            const r = await adminApi.patch(`/products/${p.id}`, payload, { headers: authHeader() });
            const np = r.data.product;
            setProducts(prev => prev.map(x => x.id === p.id ? { ...x, ...np } : x));
            setEdits(prev => { const { [p.id]: _omit, ...rest } = prev; return rest; });
            toast.success(`Saved “${p.name_en}”`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Update failed");
        } finally {
            setSavingId(null);
        }
    };

    const cellInput = (p, field, type = "text", width = "w-20") => {
        const val = edits[p.id]?.[field] ?? p[field] ?? "";
        return (
            <input
                data-testid={`prod-${field}-${p.id}`}
                type={type}
                value={val}
                step={field === "price_all" ? "0.01" : "1"}
                min="0"
                onChange={(e) => setField(p.id, field, e.target.value)}
                className={`${width} h-8 px-2 rounded-md border-2 border-neutral-200 focus:border-neutral-900 outline-none text-sm tabular-nums`}
            />
        );
    };

    return (
        <div className="mt-1">
            {/* ─── Filters bar ─── */}
            <div className="bg-white rounded-2xl p-3 border border-neutral-200 flex flex-col md:flex-row gap-2 md:items-center">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
                    <input
                        data-testid="products-search"
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by name, product code, EAN or SKU…"
                        className="w-full h-9 pl-9 pr-3 rounded-full bg-neutral-50 border-2 border-neutral-200 text-sm outline-none focus:border-neutral-900"
                    />
                </div>
                <select
                    data-testid="products-brand-filter"
                    value={brand}
                    onChange={(e) => { setBrand(e.target.value); setPage(1); }}
                    className="h-9 px-3 pr-7 rounded-full bg-neutral-50 border-2 border-neutral-200 text-sm outline-none focus:border-neutral-900 min-w-[150px]"
                >
                    <option value="">All brands</option>
                    {facets.brands.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
                <select
                    data-testid="products-category-filter"
                    value={category}
                    onChange={(e) => { setCategory(e.target.value); setPage(1); }}
                    className="h-9 px-3 pr-7 rounded-full bg-neutral-50 border-2 border-neutral-200 text-sm outline-none focus:border-neutral-900 min-w-[180px]"
                >
                    <option value="">All categories</option>
                    {facets.categories.map(c => (
                        <option key={c.id} value={c.id}>{c.label} ({c.count})</option>
                    ))}
                </select>
                {(search || brand || category) && (
                    <button
                        data-testid="products-clear-filters"
                        onClick={() => { setSearch(""); setBrand(""); setCategory(""); setPage(1); }}
                        className="h-9 px-3 rounded-full text-xs font-semibold text-neutral-600 hover:bg-neutral-100"
                    >
                        Clear
                    </button>
                )}
                <div className="text-sm text-neutral-500 md:ml-auto whitespace-nowrap">
                    {total} product{total === 1 ? "" : "s"}
                </div>
            </div>

            {/* ─── Table ─── */}
            <div className="mt-4 bg-white rounded-2xl border border-neutral-200 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-neutral-50 sticky top-0">
                        <tr className="text-left">
                            <th className="px-3 py-3 w-14"></th>
                            <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-neutral-500">Product</th>
                            <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-neutral-500">Product Code</th>
                            <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-neutral-500">EAN Barcode</th>
                            <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-neutral-500">Brand</th>
                            <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-neutral-500">Category</th>
                            <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-neutral-500 text-right">Price (L)</th>
                            <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-neutral-500 text-right">Discount %</th>
                            <th className="px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-neutral-500 text-right">Stock</th>
                            <th className="px-3 py-3 w-24"></th>
                        </tr>
                    </thead>
                    <tbody data-testid="admin-products-table">
                        {loading ? (
                            <tr><td colSpan={10} className="px-4 py-10 text-center text-neutral-500 text-sm">
                                <RefreshCw className="h-5 w-5 animate-spin mx-auto" />
                            </td></tr>
                        ) : products.length === 0 ? (
                            <tr><td colSpan={10} className="px-4 py-10 text-center text-neutral-500 text-sm">No products match.</td></tr>
                        ) : products.map((p) => {
                            const dirty = !!edits[p.id] && Object.keys(edits[p.id]).length > 0;
                            return (
                                <tr key={p.id} data-testid={`admin-product-row-${p.id}`} className="border-t border-neutral-100 hover:bg-neutral-50/50">
                                    <td className="px-3 py-2 align-top">
                                        <label
                                            data-testid={`prod-image-upload-${p.id}`}
                                            className="relative block h-10 w-10 rounded-md overflow-hidden bg-neutral-100 cursor-pointer group/img"
                                            title="Click to upload a new image"
                                        >
                                            {p.image && <img src={p.image} alt={p.name_en} className="h-full w-full object-cover" loading="lazy" />}
                                            <span className="absolute inset-0 bg-black/55 opacity-0 group-hover/img:opacity-100 flex items-center justify-center transition-opacity">
                                                <Camera className="h-4 w-4 text-white" />
                                            </span>
                                            <input
                                                type="file"
                                                accept="image/png,image/jpeg,image/webp,image/gif"
                                                className="hidden"
                                                disabled={savingId === p.id}
                                                onChange={(e) => {
                                                    const f = e.target.files?.[0];
                                                    e.target.value = "";  // allow re-uploading the same file
                                                    uploadImage(p, f);
                                                }}
                                            />
                                        </label>
                                    </td>
                                    <td className="px-3 py-2 align-top">
                                        <div className="font-semibold text-neutral-900 text-[13px] leading-tight">{p.name_en}</div>
                                        {p.sku && (
                                            <div className="font-mono text-[10px] text-neutral-500 mt-0.5">SKU: {p.sku}</div>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 align-top">
                                        {cellInput(p, "product_code", "text", "w-32")}
                                    </td>
                                    <td className="px-3 py-2 align-top">
                                        {cellInput(p, "ean_barcode", "text", "w-36")}
                                    </td>
                                    <td className="px-3 py-2 align-top text-[12px] text-neutral-700">{p.brand || "—"}</td>
                                    <td className="px-3 py-2 align-top">
                                        <select
                                            data-testid={`prod-category-${p.id}`}
                                            value={edits[p.id]?.category ?? p.category ?? ""}
                                            onChange={(e) => setField(p.id, "category", e.target.value)}
                                            className="w-44 h-8 px-2 rounded-md border-2 border-neutral-200 focus:border-neutral-900 outline-none text-[12px] bg-white"
                                        >
                                            {facets.categories.map(c => (
                                                <option key={c.id} value={c.id}>{c.label}</option>
                                            ))}
                                            {/* Fallback when product's current slug isn't in the facets list yet */}
                                            {p.category && !facets.categories.some(c => c.id === p.category) && (
                                                <option value={p.category}>{p.category_label || p.category}</option>
                                            )}
                                        </select>
                                    </td>
                                    <td className="px-3 py-2 align-top text-right">
                                        {cellInput(p, "price_all", "number", "w-24")}
                                    </td>
                                    <td className="px-3 py-2 align-top text-right">
                                        {cellInput(p, "discount_pct", "number", "w-20")}
                                    </td>
                                    <td className="px-3 py-2 align-top text-right">
                                        {cellInput(p, "stock", "number", "w-20")}
                                    </td>
                                    <td className="px-3 py-2 align-top text-right">
                                        <button
                                            data-testid={`prod-save-${p.id}`}
                                            disabled={!dirty || savingId === p.id}
                                            onClick={() => saveRow(p)}
                                            className={`h-8 px-3 rounded-md text-xs font-semibold ${dirty
                                                ? "bg-neutral-900 text-white hover:bg-neutral-800"
                                                : "bg-neutral-100 text-neutral-400"
                                            } disabled:opacity-50`}
                                        >
                                            {savingId === p.id ? "…" : "Save"}
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {pages > 1 && (
                <div className="mt-4 flex items-center justify-between text-sm">
                    <div className="text-neutral-500">Page {page} of {pages}</div>
                    <div className="flex items-center gap-2">
                        <button
                            disabled={page <= 1}
                            onClick={() => setPage(p => p - 1)}
                            data-testid="products-prev-page"
                            className="h-9 w-9 rounded-lg bg-white border border-neutral-200 disabled:opacity-40 flex items-center justify-center"
                        ><ChevronLeft className="h-4 w-4" /></button>
                        <button
                            disabled={page >= pages}
                            onClick={() => setPage(p => p + 1)}
                            data-testid="products-next-page"
                            className="h-9 w-9 rounded-lg bg-white border border-neutral-200 disabled:opacity-40 flex items-center justify-center"
                        ><ChevronRight className="h-4 w-4" /></button>
                    </div>
                </div>
            )}
        </div>
    );
};

const BulkUploadDialog = ({ open, onClose, onDone }) => {
    const fileRef = useRef(null);
    const [file, setFile] = useState(null);
    const [replace, setReplace] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState(null);

    const reset = () => {
        setFile(null);
        setReplace(false);
        setResult(null);
        if (fileRef.current) fileRef.current.value = "";
    };

    const handleClose = () => {
        if (uploading) return;
        reset();
        onClose();
    };

    const handleUpload = async () => {
        if (!file) {
            toast.error("Please choose a CSV file first");
            return;
        }
        setUploading(true);
        setResult(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const token = localStorage.getItem("bb_admin_token");
            const r = await axios.post(
                `${BASE}/api/admin/products/bulk-upload?replace=${replace ? "true" : "false"}`,
                fd,
                { headers: { Authorization: `Bearer ${token}` } },
            );
            setResult(r.data);
            toast.success(`Imported: ${r.data.inserted} new, ${r.data.updated} updated`);
            onDone && onDone(r.data);
        } catch (err) {
            const msg = err.response?.data?.detail || err.message || "Upload failed";
            toast.error(msg);
            setResult({ error: msg });
        } finally {
            setUploading(false);
        }
    };

    if (!open) return null;
    return (
        <div
            data-testid="bulk-upload-dialog"
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={(e) => e.target === e.currentTarget && handleClose()}
        >
            <div className="bg-white rounded-3xl w-full max-w-lg p-7 shadow-2xl relative">
                <button
                    data-testid="bulk-upload-close"
                    onClick={handleClose}
                    disabled={uploading}
                    className="absolute top-4 right-4 h-9 w-9 rounded-full bg-neutral-100 hover:bg-neutral-200 flex items-center justify-center disabled:opacity-40"
                    aria-label="Close"
                >
                    <X className="h-4 w-4" />
                </button>
                <div className="h-11 w-11 rounded-2xl bg-neutral-900 text-white flex items-center justify-center">
                    <Upload className="h-5 w-5" />
                </div>
                <h2 className="font-display text-2xl font-medium tracking-tight mt-3">
                    Bulk upload products
                </h2>
                <p className="text-neutral-500 text-sm mt-1">
                    Upload a CSV with the columns: <span className="font-mono text-[11px]">name, description, Weight (gm), price, sku, stock, category, brand, tags, discount, variants, image_url</span>.
                </p>

                <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-500 mt-5 mb-2">
                    CSV file
                </label>
                <input
                    ref={fileRef}
                    type="file"
                    accept=".csv,text/csv"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    data-testid="bulk-upload-file-input"
                    disabled={uploading}
                    className="w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-neutral-900 file:text-white hover:file:bg-neutral-800"
                />

                <label className="mt-4 flex items-start gap-2.5 cursor-pointer select-none">
                    <input
                        type="checkbox"
                        checked={replace}
                        onChange={(e) => setReplace(e.target.checked)}
                        data-testid="bulk-upload-replace"
                        disabled={uploading}
                        className="mt-1 h-4 w-4 rounded border-2 border-neutral-300 text-neutral-900 focus:ring-neutral-900/30 cursor-pointer"
                    />
                    <span className="text-sm text-neutral-700">
                        <span className="font-semibold">Replace all existing products</span>
                        <span className="block text-[12px] text-neutral-500 mt-0.5">
                            When checked, the entire product catalog is wiped first. Leave unchecked to UPSERT by SKU.
                        </span>
                    </span>
                </label>

                {result && !result.error && (
                    <div
                        data-testid="bulk-upload-summary"
                        className="mt-5 rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-sm"
                    >
                        <div className="font-semibold text-emerald-800">Import summary</div>
                        <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-[13px]">
                            <dt className="text-emerald-700">CSV rows</dt>
                            <dd className="font-semibold text-emerald-900">{result.csv_rows}</dd>
                            <dt className="text-emerald-700">Inserted</dt>
                            <dd className="font-semibold text-emerald-900">{result.inserted}</dd>
                            <dt className="text-emerald-700">Updated</dt>
                            <dd className="font-semibold text-emerald-900">{result.updated}</dd>
                            <dt className="text-emerald-700">Skipped</dt>
                            <dd className="font-semibold text-emerald-900">{result.skipped}</dd>
                            {result.deleted_before_import > 0 && (
                                <>
                                    <dt className="text-emerald-700">Deleted (replace mode)</dt>
                                    <dd className="font-semibold text-emerald-900">{result.deleted_before_import}</dd>
                                </>
                            )}
                            <dt className="text-emerald-700">Total in catalog</dt>
                            <dd className="font-semibold text-emerald-900">{result.total_in_db}</dd>
                        </dl>
                        {result.errors?.length > 0 && (
                            <details className="mt-3">
                                <summary className="text-rose-700 text-xs font-semibold cursor-pointer">
                                    {result.errors.length} row{result.errors.length === 1 ? "" : "s"} had problems
                                </summary>
                                <ul className="mt-1.5 text-[12px] text-rose-700 list-disc list-inside max-h-32 overflow-y-auto">
                                    {result.errors.map((e, i) => (
                                        <li key={i}>row {e.row}: {e.reason}</li>
                                    ))}
                                </ul>
                            </details>
                        )}
                    </div>
                )}

                <div className="mt-6 flex gap-2">
                    <button
                        onClick={handleUpload}
                        disabled={uploading || !file}
                        data-testid="bulk-upload-submit"
                        className="flex-1 h-12 rounded-xl bg-neutral-900 hover:bg-neutral-800 text-white font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2"
                    >
                        {uploading ? (
                            <><RefreshCw className="h-4 w-4 animate-spin" /> Uploading…</>
                        ) : (
                            <><Upload className="h-4 w-4" /> Upload CSV</>
                        )}
                    </button>
                    <button
                        onClick={handleClose}
                        disabled={uploading}
                        className="h-12 px-5 rounded-xl border-2 border-neutral-300 text-neutral-700 font-semibold hover:bg-neutral-50 disabled:opacity-40"
                    >
                        {result ? "Done" : "Cancel"}
                    </button>
                </div>
            </div>
        </div>
    );
};

const AdminLoginScreen = ({ onSuccess }) => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const r = await adminApi.post("/login", { email, password });
            localStorage.setItem("bb_admin_token", r.data.token);
            localStorage.setItem("bb_admin_email", r.data.email);
            onSuccess(r.data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Login failed");
        } finally {
            setLoading(false);
        }
    };
    return (
        <div className="min-h-screen bg-neutral-950 flex items-center justify-center p-4" data-testid="admin-login-screen">
            <form onSubmit={submit} className="bg-white rounded-3xl shadow-2xl p-9 w-full max-w-sm">
                <div className="h-12 w-12 rounded-2xl bg-neutral-900 text-white flex items-center justify-center mx-auto">
                    <Package className="h-6 w-6" />
                </div>
                <h1 className="font-display text-2xl font-medium tracking-tight text-center mt-5">
                    Admin sign in
                </h1>
                <p className="text-neutral-500 text-sm text-center mt-1">
                    mybazaarcart dashboard
                </p>
                <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500 mt-7 mb-2">
                    Email
                </label>
                <input
                    data-testid="admin-email-input"
                    required
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full h-12 px-4 rounded-xl bg-neutral-50 border-2 border-neutral-200 focus:bg-white focus:border-neutral-900 outline-none"
                />
                <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500 mt-4 mb-2">
                    Password
                </label>
                <input
                    data-testid="admin-password-input"
                    required
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full h-12 px-4 rounded-xl bg-neutral-50 border-2 border-neutral-200 focus:bg-white focus:border-neutral-900 outline-none"
                />
                <button
                    type="submit"
                    disabled={loading}
                    data-testid="admin-login-btn"
                    className="mt-6 w-full h-12 rounded-xl bg-neutral-900 hover:bg-neutral-800 text-white font-semibold disabled:opacity-60"
                >
                    {loading ? "Signing in…" : "Sign in"}
                </button>
            </form>
        </div>
    );
};

const AdminDashboard = ({ email, onLogout }) => {
    const [stats, setStats] = useState(null);
    const [orders, setOrders] = useState([]);
    const [total, setTotal] = useState(0);
    const [pages, setPages] = useState(0);
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [savingId, setSavingId] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [tab, setTab] = useState("orders");  // "orders" | "products"

    const token = () => localStorage.getItem("bb_admin_token");
    const authHeader = () => ({ Authorization: `Bearer ${token()}` });

    const load = useCallback(async () => {
        if (tab !== "orders") return;
        setLoading(true);
        try {
            const [s, o] = await Promise.all([
                adminApi.get("/stats", { headers: authHeader() }),
                adminApi.get("/orders", {
                    headers: authHeader(),
                    params: { status: statusFilter, q: search || undefined, page, limit: 25 },
                }),
            ]);
            setStats(s.data);
            setOrders(o.data.orders);
            setTotal(o.data.total);
            setPages(o.data.pages);
        } catch (err) {
            if (err.response?.status === 401) {
                onLogout();
            } else {
                toast.error("Could not load orders");
            }
        } finally {
            setLoading(false);
        }
    }, [tab, statusFilter, search, page, onLogout]);

    useEffect(() => { load(); }, [load]);

    const updateStatus = async (orderId, newStatus) => {
        setSavingId(orderId);
        try {
            await adminApi.patch(`/orders/${orderId}`, { status: newStatus }, { headers: authHeader() });
            toast.success(`Updated to ${newStatus}`);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Update failed");
        } finally {
            setSavingId(null);
        }
    };

    return (
        <div className="min-h-screen bg-neutral-100" data-testid="admin-dashboard">
            <header className="bg-neutral-900 text-white py-4 px-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-xl bg-[#FF5722] flex items-center justify-center">
                        <Package className="h-5 w-5" />
                    </div>
                    <div>
                        <div className="font-display text-lg font-medium">mybazaarcart admin</div>
                        <div className="text-[11px] text-neutral-400">{email}</div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setBulkOpen(true)}
                        data-testid="admin-bulk-upload-open"
                        className="h-9 px-3 rounded-lg bg-[#FF5722] hover:bg-[#E64A19] flex items-center gap-2 text-sm font-semibold"
                    >
                        <Upload className="h-4 w-4" />
                        <span className="hidden sm:inline">Bulk upload</span>
                    </button>
                    <button
                        onClick={load}
                        data-testid="admin-refresh"
                        className="h-9 w-9 rounded-lg bg-neutral-800 hover:bg-neutral-700 flex items-center justify-center"
                    >
                        <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                    </button>
                    <button
                        onClick={onLogout}
                        data-testid="admin-logout"
                        className="h-9 px-3 rounded-lg bg-neutral-800 hover:bg-neutral-700 flex items-center gap-2 text-sm"
                    >
                        <LogOut className="h-4 w-4" />
                        Sign out
                    </button>
                </div>
            </header>

            {/* ─── Tabs ─── */}
            <div className="bg-white border-b border-neutral-200">
                <div className="max-w-7xl mx-auto px-6 flex gap-1">
                    {[
                        { id: "orders",   label: "Orders" },
                        { id: "products", label: "Products" },
                    ].map((tb) => (
                        <button
                            key={tb.id}
                            onClick={() => setTab(tb.id)}
                            data-testid={`admin-tab-${tb.id}`}
                            className={`relative h-12 px-5 text-sm font-semibold transition-colors ${
                                tab === tb.id ? "text-neutral-900" : "text-neutral-500 hover:text-neutral-700"
                            }`}
                        >
                            {tb.label}
                            {tab === tb.id && (
                                <span className="absolute left-3 right-3 -bottom-px h-[3px] rounded-t-full bg-[#FF5722]" />
                            )}
                        </button>
                    ))}
                </div>
            </div>

            <main className="max-w-7xl mx-auto p-6">
                {tab === "products" ? (
                    <ProductsTab authHeader={authHeader} />
                ) : (
                    <>
                {/* ─── Stat cards ─── */}
                {stats && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div data-testid="stat-total-orders" className="bg-white rounded-2xl p-5 border border-neutral-200">
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Total orders</span>
                                <Package className="h-4 w-4 text-neutral-400" />
                            </div>
                            <div className="font-display text-3xl font-medium mt-2">{stats.total_orders}</div>
                        </div>
                        <div data-testid="stat-revenue" className="bg-white rounded-2xl p-5 border border-neutral-200">
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Revenue</span>
                                <DollarSign className="h-4 w-4 text-neutral-400" />
                            </div>
                            <div className="font-display text-3xl font-medium mt-2">{fmt(stats.total_revenue_all)}</div>
                        </div>
                        <div data-testid="stat-last-7d" className="bg-white rounded-2xl p-5 border border-neutral-200">
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Last 7 days</span>
                                <TrendingUp className="h-4 w-4 text-neutral-400" />
                            </div>
                            <div className="font-display text-3xl font-medium mt-2">{stats.orders_last_7d}</div>
                        </div>
                    </div>
                )}

                {/* ─── Filters ─── */}
                <div className="mt-6 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
                    <div className="flex flex-wrap gap-1.5">
                        {STATUSES.map((s) => (
                            <button
                                key={s}
                                onClick={() => { setStatusFilter(s); setPage(1); }}
                                data-testid={`admin-filter-${s}`}
                                className={`px-3 h-9 rounded-full text-xs font-semibold border-2 transition-all ${
                                    statusFilter === s
                                        ? "border-neutral-900 bg-neutral-900 text-white"
                                        : "border-neutral-300 bg-white text-neutral-700 hover:border-neutral-500"
                                }`}
                            >
                                {s.replace(/_/g, " ")}
                                {stats?.by_status?.[s] && (
                                    <span className={`ml-1.5 ${statusFilter === s ? "text-neutral-400" : "text-neutral-500"}`}>
                                        {stats.by_status[s].count}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                    <div className="relative w-full md:w-64">
                        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
                        <input
                            data-testid="admin-search"
                            type="search"
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                            placeholder="Search phone / name / id…"
                            className="w-full h-9 pl-9 pr-3 rounded-full bg-white border-2 border-neutral-200 text-sm outline-none focus:border-neutral-900"
                        />
                    </div>
                </div>

                {/* ─── Orders table ─── */}
                <div className="mt-5 bg-white rounded-2xl border border-neutral-200 overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-neutral-50">
                            <tr className="text-left">
                                <th className="w-8 px-2 py-3" />
                                <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-neutral-500">Order</th>
                                <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-neutral-500">Customer</th>
                                <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-neutral-500">Address</th>
                                <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-neutral-500">Total</th>
                                <th className="px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-neutral-500">Status</th>
                            </tr>
                        </thead>
                        <tbody data-testid="admin-orders-table">
                            {orders.length === 0 ? (
                                <tr><td colSpan={6} className="px-4 py-10 text-center text-neutral-500 text-sm">No orders match the filter.</td></tr>
                            ) : orders.map((o) => {
                                const isOpen = expandedId === o.id;
                                return (
                                <React.Fragment key={o.id}>
                                <tr
                                    data-testid={`admin-order-${o.id}`}
                                    className={`border-t border-neutral-100 hover:bg-neutral-50/50 cursor-pointer ${isOpen ? "bg-neutral-50/70" : ""}`}
                                    onClick={() => setExpandedId(isOpen ? null : o.id)}
                                >
                                    <td className="px-2 py-3 align-top">
                                        <button
                                            type="button"
                                            data-testid={`admin-order-expand-${o.id}`}
                                            onClick={(e) => { e.stopPropagation(); setExpandedId(isOpen ? null : o.id); }}
                                            className="h-7 w-7 rounded-md hover:bg-neutral-200 flex items-center justify-center"
                                            aria-label={isOpen ? "Collapse order items" : "Expand order items"}
                                        >
                                            <ChevronDown className={`h-4 w-4 text-neutral-600 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                                        </button>
                                    </td>
                                    <td className="px-4 py-3 align-top">
                                        <div className="font-mono font-bold text-xs">#{o.id.slice(0, 8).toUpperCase()}</div>
                                        <div className="text-[11px] text-neutral-500">{new Date(o.created_at).toLocaleString()}</div>
                                        <div className="text-[11px] text-neutral-500 mt-0.5">
                                            {o.items?.length || 0} items · {(o.items || []).reduce((s, it) => s + (it.qty || 0), 0)} units
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 align-top">
                                        <div className="font-semibold">{o.name || "—"}</div>
                                        <div className="text-[11px] text-neutral-500">{o.phone}</div>
                                    </td>
                                    <td className="px-4 py-3 align-top text-xs max-w-xs">
                                        <div className="line-clamp-2">{o.address}</div>
                                        {o.notes && <div className="text-[11px] text-neutral-500 mt-0.5 italic">"{o.notes}"</div>}
                                    </td>
                                    <td className="px-4 py-3 align-top">
                                        <div className="font-display font-semibold">{fmt(o.subtotal_all)}</div>
                                        <div className="text-[10px] text-neutral-500 uppercase tracking-wider">
                                            {o.payment_method || "cod"}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 align-top" onClick={(e) => e.stopPropagation()}>
                                        <select
                                            data-testid={`admin-status-select-${o.id}`}
                                            value={o.status}
                                            disabled={savingId === o.id}
                                            onChange={(e) => updateStatus(o.id, e.target.value)}
                                            className={`px-2 py-1 rounded-md text-[11px] font-semibold uppercase tracking-wider border-2 outline-none cursor-pointer ${statusBadge(o.status)}`}
                                        >
                                            {STATUSES.filter((s) => s !== "all").map((s) => (
                                                <option key={s} value={s} className="bg-white text-neutral-900">
                                                    {s.replace(/_/g, " ")}
                                                </option>
                                            ))}
                                        </select>
                                    </td>
                                </tr>
                                {isOpen && (
                                    <tr
                                        data-testid={`admin-order-items-${o.id}`}
                                        className="bg-neutral-50/70 border-t border-neutral-100"
                                    >
                                        <td colSpan={6} className="px-4 py-4">
                                            <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
                                                <div className="px-4 py-2 bg-neutral-100/60 text-[10px] font-bold uppercase tracking-[0.15em] text-neutral-600 flex items-center justify-between">
                                                    <span>Items in this order</span>
                                                    <span>
                                                        Subtotal {fmt(o.subtotal_all)}
                                                        {o.shipping_all > 0 && <> · Shipping {fmt(o.shipping_all)}</>}
                                                        {o.total_all > 0 && <> · Total {fmt(o.total_all)}</>}
                                                    </span>
                                                </div>
                                                <table className="w-full text-sm">
                                                    <thead className="bg-neutral-50">
                                                        <tr className="text-left">
                                                            <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-neutral-500 w-12">#</th>
                                                            <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-neutral-500">Item</th>
                                                            <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-neutral-500">Weight</th>
                                                            <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-neutral-500 text-center">Qty</th>
                                                            <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-neutral-500 text-right">Unit</th>
                                                            <th className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-neutral-500 text-right">Line total</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {(o.items || []).length === 0 ? (
                                                            <tr><td colSpan={6} className="px-3 py-6 text-center text-neutral-500 text-xs">
                                                                No items recorded on this order.
                                                            </td></tr>
                                                        ) : o.items.map((it, idx) => (
                                                            <tr key={`${it.id || it.sku || idx}`} className="border-t border-neutral-100" data-testid={`order-item-row-${o.id}-${idx}`}>
                                                                <td className="px-3 py-2 align-top">
                                                                    <div className="h-10 w-10 rounded-md overflow-hidden bg-neutral-100 shrink-0">
                                                                        {it.image && (
                                                                            <img src={it.image} alt={it.name} className="h-full w-full object-cover" loading="lazy" />
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td className="px-3 py-2 align-top">
                                                                    <div className="font-semibold text-neutral-900 text-[13px]">{it.name}</div>
                                                                    {it.sku && (
                                                                        <div className="font-mono text-[10px] text-neutral-500 mt-0.5">SKU: {it.sku}</div>
                                                                    )}
                                                                </td>
                                                                <td className="px-3 py-2 align-top text-xs text-neutral-600">
                                                                    {it.weight || (it.weight_grams ? `${it.weight_grams} g` : "—")}
                                                                </td>
                                                                <td className="px-3 py-2 align-top text-center text-sm font-bold tabular-nums">
                                                                    × {it.qty}
                                                                </td>
                                                                <td className="px-3 py-2 align-top text-right text-sm tabular-nums">
                                                                    {fmt(it.price_all)}
                                                                </td>
                                                                <td className="px-3 py-2 align-top text-right text-sm font-semibold tabular-nums">
                                                                    {fmt((it.price_all || 0) * (it.qty || 0))}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                                </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* ─── Pagination ─── */}
                {pages > 1 && (
                    <div className="mt-4 flex items-center justify-between text-sm">
                        <div className="text-neutral-500">
                            Page {page} of {pages} · {total} orders
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                disabled={page <= 1}
                                onClick={() => setPage((p) => p - 1)}
                                data-testid="admin-prev-page"
                                className="h-9 w-9 rounded-lg bg-white border border-neutral-200 disabled:opacity-40 flex items-center justify-center"
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </button>
                            <button
                                disabled={page >= pages}
                                onClick={() => setPage((p) => p + 1)}
                                data-testid="admin-next-page"
                                className="h-9 w-9 rounded-lg bg-white border border-neutral-200 disabled:opacity-40 flex items-center justify-center"
                            >
                                <ChevronRight className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                )}
                    </>
                )}
            </main>

            <BulkUploadDialog
                open={bulkOpen}
                onClose={() => setBulkOpen(false)}
                onDone={() => { /* main agent leaves catalog stale — admin only needs orders here */ }}
            />
        </div>
    );
};

const AdminPage = () => {
    const navigate = useNavigate();
    const [authed, setAuthed] = useState(() => !!localStorage.getItem("bb_admin_token"));
    const [email, setEmail] = useState(() => localStorage.getItem("bb_admin_email") || "");

    // Validate token on mount
    useEffect(() => {
        const token = localStorage.getItem("bb_admin_token");
        if (!token) { setAuthed(false); return; }
        adminApi.get("/me", { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => { setEmail(r.data.email); setAuthed(true); })
            .catch(() => { localStorage.removeItem("bb_admin_token"); setAuthed(false); });
    }, []);

    const handleLogout = () => {
        localStorage.removeItem("bb_admin_token");
        localStorage.removeItem("bb_admin_email");
        setAuthed(false);
    };

    if (!authed) {
        return <AdminLoginScreen onSuccess={(d) => { setEmail(d.email); setAuthed(true); }} />;
    }
    return <AdminDashboard email={email} onLogout={handleLogout} />;
};

export default AdminPage;
